'use client'

import { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ApiClient from '../../utils/ApiClient';

// Task 40 (owner requirement, verbatim after a mistaken confirmation had to be cancelled via the
// API: "we should be able to cancel via the UI"): cancelling a CONFIRMED plan voids a real,
// Amazon-accepted shipment -- not a draft. Deliberately heavier than the light window.confirm()
// used for unconfirmed/ACTIVE plans elsewhere on these pages: it restates exactly what's being
// voided and requires an explicit "I understand" checkbox before the destructive button enables.
// Shared verbatim by ShipmentRuns.js (runs table) and ShipmentPlanner.js (loaded-run view) so the
// two surfaces present the same warning for the same action.
function formatDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export default function CancelConfirmedShipmentModal({
    open,
    onClose,
    planId,
    runLabel,
    units,
    asins,
    confirmedAt,
    onCancelled,
}) {
    const [understood, setUnderstood] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [error, setError] = useState(null);

    // Reset transient state every time the dialog is (re)opened for a different plan.
    useEffect(() => {
        if (open) {
            setUnderstood(false);
            setCancelling(false);
            setError(null);
        }
    }, [open, planId]);

    const asinList = Array.isArray(asins) ? asins : [];
    const asinText = asinList.length > 0
        ? asinList.map(a => (typeof a === 'string' ? a : `${a.asin}${a.qty != null ? ` ×${a.qty}` : ''}`)).join(', ')
        : '—';

    const handleClose = () => {
        if (cancelling) return;
        onClose();
    };

    const handleCancel = async () => {
        if (!planId || !understood) return;
        setCancelling(true);
        setError(null);
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${planId}`);
            setCancelling(false);
            onCancelled && onCancelled();
        } catch (err) {
            console.error('Cancel confirmed shipment failed:', err);
            setError(err.response?.data?.error || err.message || 'Cancel failed');
            setCancelling(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div>
                            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
                                <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                            </div>
                            <div className="mt-3 text-center">
                                <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                                    Cancel this confirmed shipment?
                                </DialogTitle>
                            </div>
                        </div>

                        <div className="mt-4 text-left text-sm text-gray-700 space-y-1.5 bg-gray-50 border border-gray-200 rounded-md p-3">
                            <div><span className="text-gray-500">Plan:</span> <span className="font-mono text-xs">{planId || '—'}</span>{runLabel ? <span className="text-gray-400"> ({runLabel})</span> : null}</div>
                            <div><span className="text-gray-500">Units:</span> {units ?? '—'}</div>
                            <div><span className="text-gray-500">ASINs:</span> <span className="font-mono text-xs">{asinText}</span></div>
                            <div><span className="text-gray-500">Confirmed at:</span> {formatDateTime(confirmedAt)}</div>
                        </div>

                        <div className="mt-3 text-left text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3 leading-snug">
                            Partnered-carrier charges void free only within ~24 hours of confirmation; after that,
                            transportation charges may still apply. Cancelling cannot be undone.
                        </div>

                        {error && (
                            <div className="mt-3 text-left text-xs text-red-600">{error}</div>
                        )}

                        <label className="mt-4 flex items-start gap-2 text-left text-sm text-gray-800 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={understood}
                                onChange={(e) => setUnderstood(e.target.checked)}
                                className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                            <span>I understand this voids a confirmed shipment and cannot be undone.</span>
                        </label>

                        <div className="mt-5 sm:mt-6 flex gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={cancelling}
                                className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                            >
                                Keep shipment
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={!understood || cancelling}
                                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                            >
                                {cancelling ? 'Cancelling…' : 'Cancel shipment'}
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
